import React, { useState, useEffect } from "react";
import { PortableText } from "@portabletext/react";
import sanityClient from "../client.js";

// Custom components for Portable Text rendering
const portableTextComponents = {
    block: {
        normal: ({ children }) => (
            <p className="job-para">{children}</p>
        ),
        h3: ({ children }) => (
            <h3 className="job-para-heading">{children}</h3>
        ),
    },
    list: {
        bullet: ({ children }) => (
            <ul className="job-list-ul">{children}</ul>
        ),
        number: ({ children }) => (
            <ol className="job-list-ol">{children}</ol>
        ),
    },
    listItem: {
        bullet: ({ children }) => (
            <li className="job-list-item">{children}</li>
        ),
        number: ({ children }) => (
            <li className="job-list-item">{children}</li>
        ),
    },
    marks: {
        strong: ({ children }) => (
            <strong style={{ color: "#e2e8f0", fontWeight: 600 }}>{children}</strong>
        ),
        em: ({ children }) => (
            <em style={{ color: "#a5b4fc" }}>{children}</em>
        ),
    },
};

export default function Job() {
    const [jobData, setJobData] = useState(null);

    useEffect(() => {
        sanityClient
            .fetch(
                `*[_type == "job"] | order(order asc){
          title,
          field,
          description,
          link,
          mainImage{
            asset->{ _id, url },
            alt
          }
        }`
            )
            .then((data) => setJobData(data))
            .catch(console.error);
    }, []);

    return (
        <main className="job-root">
            <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600&family=Sora:wght@700;800&display=swap');

        .job-root {
          min-height: 100vh;
          background-color: #080e1c;
          font-family: 'Inter', system-ui, sans-serif;
          position: relative;
          overflow: hidden;
        }

        .job-root::before {
          content: '';
          position: fixed;
          inset: 0;
          background:
            radial-gradient(ellipse 70% 50% at 10% 5%, rgba(99,102,241,0.1) 0%, transparent 60%),
            radial-gradient(ellipse 50% 40% at 90% 90%, rgba(56,189,248,0.07) 0%, transparent 55%);
          pointer-events: none;
          z-index: 0;
        }

        .job-container {
          max-width: 900px;
          margin: 0 auto;
          padding: 4rem 2rem 6rem;
          position: relative;
          z-index: 1;
        }

        .job-header {
          text-align: center;
          margin-bottom: 4rem;
        }

        .job-eyebrow {
          display: inline-block;
          font-size: 0.72rem;
          font-weight: 600;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: #6366f1;
          margin-bottom: 1rem;
        }

        .job-title {
          font-family: 'Sora', system-ui, sans-serif;
          font-size: clamp(2rem, 5vw, 3rem);
          font-weight: 800;
          color: #f0f4ff;
          letter-spacing: -0.02em;
          margin-bottom: 0.75rem;
        }

        .job-title span {
          background: linear-gradient(90deg, #818cf8, #38bdf8);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .job-subtitle {
          color: #475569;
          font-size: 0.95rem;
        }

        /* Timeline line */
        .job-list {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
          position: relative;
        }

        .job-list::before {
          content: '';
          position: absolute;
          left: 0;
          top: 0;
          bottom: 0;
          width: 1px;
          background: linear-gradient(to bottom, #6366f1, rgba(56,189,248,0.3), transparent);
          margin-left: 11px;
        }

        /* Card */
        .job-card {
          position: relative;
          padding-left: 3rem;
        }

        /* Timeline dot */
        .job-card::before {
          content: '';
          position: absolute;
          left: 0;
          top: 1.75rem;
          width: 23px;
          height: 23px;
          border-radius: 50%;
          background: linear-gradient(135deg, #6366f1, #38bdf8);
          box-shadow: 0 0 0 4px rgba(99,102,241,0.15);
          z-index: 1;
        }

        .job-card-inner {
          background: #0f1829;
          border: 1px solid rgba(99,102,241,0.12);
          border-radius: 16px;
          padding: 2rem 2.25rem;
          transition: border-color 0.25s, box-shadow 0.25s, transform 0.25s;
        }

        .job-card-inner:hover {
          border-color: rgba(99,102,241,0.35);
          box-shadow: 0 8px 40px rgba(0,0,0,0.4), 0 0 30px rgba(99,102,241,0.08);
          transform: translateY(-2px);
        }

        .job-card-top {
          display: flex;
          gap: 1.5rem;
          align-items: flex-start;
          margin-bottom: 1.25rem;
        }

        .job-logo {
          width: 56px;
          height: 56px;
          border-radius: 12px;
          object-fit: contain;
          background: #fff;
          padding: 6px;
          flex-shrink: 0;
          border: 1px solid rgba(99,102,241,0.1);
        }

        .job-card-meta {
          flex: 1;
          min-width: 0;
        }

        .job-card-title {
          font-family: 'Sora', system-ui, sans-serif;
          font-size: 1.25rem;
          font-weight: 700;
          color: #e2e8f0;
          margin-bottom: 0.3rem;
          text-decoration: none;
          transition: color 0.2s;
          display: block;
        }

        .job-card-title:hover { color: #a5b4fc; }

        .job-field-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          font-size: 0.72rem;
          font-weight: 500;
          color: #38bdf8;
          background: rgba(56,189,248,0.08);
          border: 1px solid rgba(56,189,248,0.2);
          padding: 0.25rem 0.65rem;
          border-radius: 100px;
          letter-spacing: 0.03em;
        }

        /* Portable Text styles */
        .job-description {
          margin-bottom: 1.25rem;
        }

        .job-para {
          color: #64748b;
          font-size: 0.925rem;
          line-height: 1.8;
          margin: 0 0 0.85rem 0;
        }

        .job-para:last-child { margin-bottom: 0; }

        .job-para-heading {
          font-family: 'Sora', system-ui, sans-serif;
          font-size: 1rem;
          font-weight: 700;
          color: #e2e8f0;
          margin: 1rem 0 0.5rem;
        }

        .job-list-ul {
          list-style: none;
          padding-left: 0;
          margin: 0.25rem 0 0.85rem 0;
        }

        .job-list-ol {
          padding-left: 1.25rem;
          margin: 0.25rem 0 0.85rem 0;
          color: #64748b;
          font-size: 0.925rem;
          line-height: 1.8;
        }

        .job-list-item {
          position: relative;
          padding-left: 1.25rem;
          margin-bottom: 0.4rem;
          color: #64748b;
          font-size: 0.925rem;
          line-height: 1.8;
        }

        .job-list-ul .job-list-item::before {
          content: '';
          position: absolute;
          left: 0;
          top: 0.6em;
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background: #6366f1;
        }

        .job-link {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          color: #818cf8;
          font-size: 0.82rem;
          font-weight: 500;
          text-decoration: none;
          transition: color 0.2s, gap 0.2s;
        }

        .job-link:hover {
          color: #a5b4fc;
          gap: 0.6rem;
        }

        /* Loading state */
        .job-loading {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .job-skeleton {
          background: #0f1829;
          border: 1px solid rgba(99,102,241,0.08);
          border-radius: 16px;
          height: 160px;
          animation: shimmer 1.5s ease infinite;
        }

        @keyframes shimmer {
          0%, 100% { opacity: 0.5; }
          50%       { opacity: 0.9; }
        }

        @media (max-width: 640px) {
          .job-card-top { flex-direction: column; gap: 0.75rem; }
          .job-card-inner { padding: 1.5rem; }
          .job-list::before { display: none; }
          .job-card { padding-left: 0; }
          .job-card::before { display: none; }
        }
      `}</style>

            <div className="job-container">
                {/* Header */}
                <div className="job-header">
                    <span className="job-eyebrow">Career</span>
                    <h1 className="job-title">
                        Professional <span>Experience</span>
                    </h1>
                    <p className="job-subtitle">
                        Companies and teams I've had the pleasure of building with
                    </p>
                </div>

                {/* Job cards */}
                {!jobData ? (
                    <div className="job-loading">
                        {[1, 2, 3].map(i => <div key={i} className="job-skeleton" />)}
                    </div>
                ) : (
                    <div className="job-list">
                        {jobData.map((job, index) => (
                            <div className="job-card" key={index}>
                                <div className="job-card-inner">
                                    <div className="job-card-top">
                                        {job.mainImage?.asset?.url && (
                                            <img
                                                src={job.mainImage.asset.url}
                                                alt={job.mainImage.alt || job.title}
                                                className="job-logo"
                                            />
                                        )}
                                        <div className="job-card-meta">
                                            <a
                                                href={job.link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="job-card-title"
                                            >
                                                {job.title}
                                            </a>
                                            {job.field && (
                                                <span className="job-field-badge">
                                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                                        <circle cx="12" cy="12" r="3" />
                                                        <path d="M12 2v2m0 16v2M2 12h2m16 0h2" />
                                                    </svg>
                                                    {job.field}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Portable Text description */}
                                    <div className="job-description">
                                        {Array.isArray(job.description) ? (
                                            <PortableText
                                                value={job.description}
                                                components={portableTextComponents}
                                            />
                                        ) : (
                                            // Fallback for old plain text entries
                                            job.description?.split('\n\n').filter(Boolean).map((para, i) => (
                                                <p className="job-para" key={i}>{para}</p>
                                            ))
                                        )}
                                    </div>

                                    <a
                                        href={job.link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="job-link"
                                    >
                                        Visit organisation website
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                            <line x1="5" y1="12" x2="19" y2="12" />
                                            <polyline points="12 5 19 12 12 19" />
                                        </svg>
                                    </a>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </main>
    );
}